import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { LoggerService } from 'shared/core/services/logger.service';
import { ServerService } from 'shared/core/services/server.service';
import {
    TENANT_STORAGE_KEY,
    TenantConfigService,
} from 'shared/core/services/tenant/tenant-config.service';
import { UserService } from 'shared/core/services/user/user.service';
import { User } from 'shared/interfaces/UserInterface';

@Injectable({
    providedIn: 'root',
})
export class TenantService {
    private static readonly STORAGE_KEY = TENANT_STORAGE_KEY;

    private oauthConfigs = new Map<string, any>();

    constructor(
        private logger: LoggerService,
        private tenantConfigService: TenantConfigService,
        private userService: UserService,
        private serverService: ServerService
    ) {
        // Restore tenant header from localStorage before user is authenticated
        const stored = localStorage.getItem(TenantService.STORAGE_KEY);
        if (stored) {
            this.logger.info(
                '[TenantService] Restoring tenant from localStorage:',
                stored
            );
            this.serverService.setHeaderSafely('x-tenant-id', stored, true);
        }

        // When config loads, sync the x-tenant-id header (config$ emits non-null only)
        this.tenantConfigService.config$.subscribe(config => {
            if (config.id) {
                this.serverService.setHeaderSafely('x-tenant-id', config.id, true);
                this.persistTenant(config.id);
            }
        });

        // When user is ready, load config.
        // on_ready is a BehaviorSubject(false): without the filter this fired
        // immediately on construction with a null user.
        this.userService.on_ready.pipe(filter(ready => ready)).subscribe(() => {
            this.setTenantFromUser(this.userService.user);
        });
    }

    // ---------------------------------------------------------------------------
    // Admin API helpers
    // ---------------------------------------------------------------------------

    // All tenant administration goes through the backend, NOT straight to the sidecar.
    // The backend is what enforces the AccessType gates and what holds
    // the sidecar's x-internal-secret (fetched from Key Vault, never sent to a
    // browser). Calling the sidecar from here would mean the multi-tenant admin
    // is protected only by hiding its tab.
    //
    //   /api/tenant-admin/*   -> TenantAdminController   (PLATFORM_ADMIN, cross-tenant)
    //   /api/admin/tenant/*   -> admin/tenant/TenantAdmin (ADMIN, own tenant only)
    //
    // ServerService is used rather than HttpClient so these inherit the shared x-access-token / x-csrf-token / x-tenant-id header handling.

    private apiGet<T = any[]>(path: string): Observable<T> {
        return new Observable<T>(subscriber => {
            this.serverService.get(
                path,
                {},
                data => {
                    subscriber.next(data as T);
                    subscriber.complete();
                },
                err => subscriber.error(err)
            );
        });
    }

    private apiPost<T = any[]>(path: string, body: object = {}): Observable<T> {
        return new Observable<T>(subscriber => {
            this.serverService.post(
                path,
                body,
                data => {
                    subscriber.next(data as T);
                    subscriber.complete();
                },
                err => subscriber.error(err)
            );
        });
    }

    // ---- Cross-tenant (PLATFORM_ADMIN) --------------------------------------
    // Paths are the controller method names, which is what AbstractController registers: /api/ + url() + '/' + fn.name.

    public getTenantList(): Observable<any[]> {
        return this.apiGet('tenant-admin/tenantsList');
    }
    public getLastUpdate(): Observable<{ moddate: string; user: string } | null> {
        return this.apiGet('tenant-admin/lastUpdate');
    }
    public getActiveSubscriptionsByTenant(): Observable<any[]> {
        return this.apiGet('tenant-admin/activeSubscriptionsByTenant');
    }
    public getUsersByTenant(): Observable<any[]> {
        return this.apiGet('tenant-admin/usersByTenant');
    }
    public getSubscriptionsList(): Observable<any[]> {
        return this.apiGet('tenant-admin/subscriptionsList');
    }
    public getAuditLog(): Observable<any[]> {
        return this.apiGet('tenant-admin/auditLog');
    }

    // ---- Own tenant (ADMIN) -------------------------------------------------
    // tenant_id is resolved from the session server-side and deliberately not sent from here — otherwise any admin could read another tenant's hotels.

    public getTenantInfo(): Observable<any[]> {
        return this.apiGet('admin/tenant/tenant_info');
    }
    public getSubscriptionsSummary(): Observable<any[]> {
        return this.apiGet('admin/tenant/subscriptions_summary');
    }
    public getHotelsList(): Observable<any[]> {
        return this.apiGet('admin/tenant/hotels_list');
    }
    public getHotelAuditLog(): Observable<any[]> {
        return this.apiGet('admin/tenant/audit_log');
    }

    // ---- Writes -------------------------------------------------------------
    // user_email is injected from the session by the backend; sending it from
    // the client would let anyone forge tenant_audit.changed_by.

    public addTenant(payload: {
        tenant_id: string;
        tenant_name: string;
        tenant_admin_email?: string;
    }): Observable<any[]> {
        return this.apiPost('tenant-admin/addTenant', payload);
    }

    public removeTenant(tenant_id: string): Observable<any[]> {
        return this.apiPost('tenant-admin/removeTenant', { tenant_id });
    }

    public updateTenant(
        tenant_id: string,
        new_data: { tenant_name?: string; tenant_admin_email?: string }
    ): Observable<any[]> {
        return this.apiPost('tenant-admin/updateTenant', { tenant_id, new_data });
    }

    public upsertSubscription(payload: {
        tenant_id: string;
        date_start: string;
        date_end: string;
        product: string;
        quantity: number;
    }): Observable<any[]> {
        return this.apiPost('tenant-admin/upsertSubscription', payload);
    }

    public removeSubscription(
        tenant_id: string,
        date_start: string,
        date_end: string
    ): Observable<any[]> {
        return this.apiPost('tenant-admin/removeSubscription', {
            tenant_id,
            date_start,
            date_end,
        });
    }

    /**
     * Pre-login: the auth page and tenant picker need this before a session
     * exists, so it is served by AccessController at AccessType.NONE rather
     * than by TenantAdminController.
     */
    getOAuthConfigs(): Observable<Map<string, any>> {
        return this.apiGet<{ configs: any[] }>('access/oauthConfigs').pipe(
            map(data => data?.configs ?? []),
            map(configs => {
                this.oauthConfigs.clear();
                for (const c of configs) {
                    this.oauthConfigs.set(c.tenant_id.toLowerCase(), c.oauth);
                }
                return this.oauthConfigs;
            })
        );
    }

    /**
     * @param _tenantId ignored — the backend resolves the tenant from the
     * session. Kept in the signature so existing call sites still compile.
     */
    getTenantActiveProducts(_tenantId?: string): Observable<string[]> {
        return this.apiGet<string[]>('admin/tenant/active_products').pipe(
            map(products => products ?? [])
        );
    }

    public getOAuthConfig(tenantId: string): any {
        return this.oauthConfigs.get(tenantId.toLowerCase());
    }

    // ---------------------------------------------------------------------------
    // Tenant context
    // ---------------------------------------------------------------------------

    public async setTenantFromUser(user: User): Promise<void> {
        if (!user) {
            this.logger.warn('[TenantService] Cannot set tenant from null user');
            return;
        }

        this.logger.info('[TenantService] Setting tenant from user:', user.tenant_id);
        this.persistTenant(user.tenant_id);
        // Mirror setTenantIdForAuth(): loadConfig() below hits tenant/getConfig,
        // which needs x-tenant-id. Persisting to localStorage alone left the
        // header unset on any session without a previously stored tenant.
        if (user.tenant_id) {
            this.serverService.setHeaderSafely('x-tenant-id', user.tenant_id, true);
        }

        try {
            await this.tenantConfigService.loadConfig();
        } catch (error) {
            this.logger.warn('[TenantService] Failed to load tenant config:', error);
        }
    }

    public async setTenantIdForAuth(tenantId: string): Promise<void> {
        this.logger.info('[TenantService] Pre-auth tenant context set:', tenantId);
        this.persistTenant(tenantId);
        this.serverService.setHeaderSafely('x-tenant-id', tenantId, true);

        try {
            await this.tenantConfigService.loadConfig();
        } catch (error) {
            this.logger.warn('[TenantService] Failed to load tenant config:', error);
        }
    }

    // ---------------------------------------------------------------------------
    // Delegated to TenantConfigService
    // ---------------------------------------------------------------------------

    public getTenantId(): string | null {
        return this.tenantConfigService.getConfig()?.id ?? null;
    }

    public isTenantFeatureEnabled(feature: string): boolean {
        return this.tenantConfigService.getConfig()?.features?.includes(feature) ?? false;
    }

    public getTenantSetting<T = any>(key: string, defaultValue?: T): T {
        return (
            this.tenantConfigService.getConfig()?.settings?.[key] ?? (defaultValue as T)
        );
    }

    // ---------------------------------------------------------------------------
    // Persistence
    // ---------------------------------------------------------------------------

    public persistTenant(tenantId: string): void {
        if (tenantId) {
            localStorage.setItem(TenantService.STORAGE_KEY, tenantId);
        }
    }

    public clearTenant(): void {
        this.logger.info('[TenantService] Clearing tenant context');
        localStorage.removeItem(TenantService.STORAGE_KEY);
        this.tenantConfigService.clearCache();
    }
}
